import { BundleOfferImage } from "../../../../common/BundleOfferDialog/components/BundleOfferImage/BundleOfferImage";
import { standaloneImageUrls } from "../../../../../logic/helpers/standaloneOffers";
import { resolveLocalizedText } from "../../../../../logic/helpers/localizedText";
import {
    wheelCountOptions,
    type WheelLauncher,
} from "../../../../../logic/helpers/wheel";
import { SoundButton } from "../../../../common/SoundButton/SoundButton";
import type {
    ApiCommerceChainJourney,
    ApiCommerceStandaloneOfferV2,
} from "@bridge-applications/pb-types";
import { wheelText } from "../../../../../logic/helpers/wheel";
import { useTranslation } from "react-i18next";
import inboxButton from "../../../../../assets/static/images/buttons/inbox-icon-btn.png";
import inboxFullButton from "../../../../../assets/static/images/buttons/inbox-icon-full.png";
import holyWheelLauncherIcon from "../../../../../assets/static/images/holy-wheel/holy-wheel-launcher-icon.webp";
import { ChainOfferLauncher } from "../ChainOfferLauncher/ChainOfferLauncher";
import { LauncherLabel } from "../LauncherLabel/LauncherLabel";
import "./MainPageLaunchers.scss";

export interface MainPageLaunchersProps {
    bundleOffers?: readonly ApiCommerceStandaloneOfferV2[];
    onOpenBundle?: (offer: ApiCommerceStandaloneOfferV2) => void;
    notificationCount: string;
    chainJourney: ApiCommerceChainJourney | null;
    onOpenInbox: () => void;
    onOpenChain: () => void;
    wheels: readonly WheelLauncher[];
    onOpenWheel: (wheelCode: string) => void;
}

export function MainPageLaunchers({
    bundleOffers = [],
    onOpenBundle,
    notificationCount,
    chainJourney,
    onOpenInbox,
    onOpenChain,
    wheels,
    onOpenWheel,
}: MainPageLaunchersProps) {
    const { t, i18n } = useTranslation();
    const hasNotifications = notificationCount !== "0";
    const badgeCount =
        BigInt(notificationCount) > 99n ? "99+" : notificationCount;

    return (
        <div className="main-page-launchers">
            <SoundButton
                type="button"
                className="main-page-launchers__inbox"
                aria-label={
                    hasNotifications
                        ? t("inbox.notificationTitle", {
                              count: notificationCount,
                          })
                        : t("mainPage.inbox.ariaLabel")
                }
                onClick={onOpenInbox}
            >
                <img
                    className="main-page-launchers__inbox-icon"
                    src={hasNotifications ? inboxFullButton : inboxButton}
                    alt=""
                    draggable={false}
                />
                {hasNotifications && (
                    <span
                        className="main-page-launchers__inbox-count"
                        aria-hidden="true"
                    >
                        {badgeCount}
                    </span>
                )}
                <span className="main-page-launchers__inbox-label">
                    <LauncherLabel text={t("mainPage.inbox.label")} />
                </span>
            </SoundButton>
            {chainJourney && (
                <ChainOfferLauncher
                    journey={chainJourney}
                    onOpen={onOpenChain}
                />
            )}
            {bundleOffers.map((offer) => {
                const dialog = offer.presentation.dialog;
                if (dialog?.kind !== "BUNDLE") return null;
                const locale = i18n.resolvedLanguage ?? i18n.language;
                return (
                    <SoundButton
                        key={offer.offerId}
                        type="button"
                        className="main-page-launchers__bundle"
                        aria-label={resolveLocalizedText(dialog.title, locale)}
                        onClick={() => onOpenBundle?.(offer)}
                    >
                        <BundleOfferImage
                            className="main-page-launchers__bundle-image"
                            sources={standaloneImageUrls(
                                dialog.artwork.header.images,
                            )}
                            locale={locale}
                            alt={resolveLocalizedText(dialog.title, locale)}
                        />
                    </SoundButton>
                );
            })}
            {wheels.map((wheel) => (
                <SoundButton
                    key={wheel.wheelCode}
                    type="button"
                    className="main-page-launchers__holy-wheel"
                    aria-label={wheelText(
                        wheel.presentation.title,
                        i18n.resolvedLanguage ?? "en",
                    )}
                    onClick={() => onOpenWheel(wheel.wheelCode)}
                >
                    <img
                        className="main-page-launchers__holy-wheel-icon"
                        src={
                            wheel.presentation.launcherImageUrl ??
                            holyWheelLauncherIcon
                        }
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                    />
                    <span className="main-page-launchers__holy-wheel-label">
                        <LauncherLabel
                            text={
                                BigInt(wheel.availableSpins) > 0n
                                    ? t(
                                          "holyWheel.launcherSpins",
                                          wheelCountOptions(
                                              wheel.availableSpins,
                                          ),
                                      )
                                    : t("holyWheel.launcherLabel")
                            }
                        />
                    </span>
                </SoundButton>
            ))}
        </div>
    );
}
